import { useState, useEffect } from 'react';
import { apiClient, type PublicSettingsResponse } from '../services/api-client';

interface UsePublicSettingsReturn {
  settings: PublicSettingsResponse | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch public settings for frontend business logic
 * 
 * Fetches configuration settings needed by the frontend to implement
 * challenge access rules, specifically the paywall setting.
 * 
 * No authentication required - this is public configuration data.
 */
export function usePublicSettings(): UsePublicSettingsReturn {
  const [settings, setSettings] = useState<PublicSettingsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchPublicSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.getPublicSettings();
      setSettings(response);
    } catch (err) {
      console.error('Failed to fetch public settings:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch public settings'));
      
      // Fallback to defaults on error to prevent breaking frontend functionality
      setSettings({
        paywall: false // Default: paywall disabled for backward compatibility
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPublicSettings();
  }, []); // No dependencies - fetch once on mount

  return {
    settings,
    loading,
    error,
    refetch: fetchPublicSettings
  };
}